import client from "services/ngage/api-client";
import {LessonPlan, LessonPlanData} from "types/LessonPlan";
import DataPage from "types/DataPage";
import {dateTime, duration} from "services/date";

/**
 * Transform the given lesson plan by deserializing object properties
 *
 * @internal
 * @param lessonPlan
 */
function deserialize(lessonPlan: LessonPlan): LessonPlan {
    lessonPlan.duration = duration(lessonPlan.duration) || lessonPlan.duration
    lessonPlan.published_at = dateTime(lessonPlan.published_at)
    lessonPlan.created_at = dateTime(lessonPlan.created_at)
    lessonPlan.updated_at = dateTime(lessonPlan.updated_at)

    if(lessonPlan.activities){
        lessonPlan.activities = lessonPlan.activities.map(activityPlan => {
            activityPlan.duration = duration(activityPlan.duration) || activityPlan.duration
            activityPlan.created_at = dateTime(activityPlan.created_at)
            activityPlan.updated_at = dateTime(activityPlan.updated_at)

            return activityPlan
        })
    }

    return lessonPlan;
}

/**
 * Get all lesson plans (paginated)
 */
const all = async (page: number = 1) => {
    const response = await client.get<DataPage<LessonPlan> | null>(`/lesson-plans?page=${page}`)

    if (response.data) {
        response.data.data = response.data.data.map(lessonPlan => deserialize(lessonPlan))
    }

    return response.data
}

/**
 * Get a single lesson plan
 *
 * @param {number} id
 */
const get = async (id: number) => {
    const response = await client.get<LessonPlan>(`/lesson-plans/${id}`)
    return response.data ? deserialize(response.data) : undefined;
}

/**
 * Store a new lesson plan
 *
 * @param data
 */
const store = async (data: LessonPlanData) => {
    const response = await client.post<LessonPlan>('/lesson-plans', data)
    return response.data ? deserialize(response.data) : undefined;
}

/**
 * Update an existing lesson plan
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: LessonPlanData) => {
    const response = await client.put<LessonPlan>(`/lesson-plans/${id}`, data)
    return response.data ? deserialize(response.data) : undefined;
}

/**
 * Permanently delete a lesson plan
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    const response = await client.delete<null>(`/lesson-plans/${id}`)
    return response.status === 204
}

const lessonPlans = {
    all,
    get,
    store,
    update,
    destroy
}

export default lessonPlans;